import React, { useState } from 'react';
import { Alert } from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';
import { 
    ReceivedPendingRequest, 
    SentPendingRequest, 
    respondToRequest, 
    cancelSentRequest 
} from '../../services/userService';
import RequestCard from './RequestCard';
import ErrorAlert from '../common/ErrorAlert';
import LoadingSpinner from '../common/LoadingSpinner';

interface RequestListProps {
    requests: ReceivedPendingRequest[] | SentPendingRequest[];
    type: 'received' | 'sent';
}

// Define the type for the variables passed to the respondToRequest mutation
interface RespondVariables {
    requestId: number;
    action: 'accept' | 'decline';
}

const RequestList: React.FC<RequestListProps> = ({ requests, type }) => {
    const queryClient = useQueryClient();
    const [processingId, setProcessingId] = useState<number | null>(null);

    // --- Mutation for Responding (Accept/Decline) --- 
    const { 
        mutate: respondMutate,
        isLoading: isResponding,
        error: respondError 
    } = useMutation<
        { success: boolean; message: string }, // Return type
        Error, // Error type
        RespondVariables // Variables type
    >(
        // Adapter function: takes the single variables object
        // and calls the original function with separate arguments
        (variables: RespondVariables) => respondToRequest(variables.requestId, variables.action),
        {
            onMutate: (variables) => {
                setProcessingId(variables.requestId);
            },
            onSuccess: (data, variables) => {
                console.log(`Request ${variables.action}ed successfully:`, data);
                queryClient.invalidateQueries('pendingRequests');
                if (variables.action === 'accept') {
                    queryClient.invalidateQueries('connections');
                }
            },
            onError: (error: any) => {
                console.error('Failed to respond to request:', error);
            },
            onSettled: () => {
                setProcessingId(null);
            }
        }
    );

    // --- Mutation for Canceling Sent Request --- 
    // This one is okay as cancelSentRequest already takes a single 'number' argument
    const { 
        mutate: cancelMutate,
        isLoading: isCanceling,
        error: cancelError 
    } = useMutation<
        { success: boolean; message: string }, 
        Error, 
        number 
    >(cancelSentRequest, {
        onMutate: (requestId) => {
            setProcessingId(requestId);
        },
        onSuccess: (data, requestId) => {
            console.log('Request cancelled successfully:', data);
            queryClient.invalidateQueries('pendingRequests');
        },
        onError: (error: any) => {
            console.error('Failed to cancel request:', error);
        },
        onSettled: () => {
            setProcessingId(null);
        }
    });

    const handleAccept = (requestId: number) => {
        // Call mutate with the single object argument
        respondMutate({ requestId, action: 'accept' });
    };

    const handleDecline = (requestId: number) => {
        // Call mutate with the single object argument
        respondMutate({ requestId, action: 'decline' });
    };

    const handleCancel = (requestId: number) => {
        // Call mutate with the single number argument
        cancelMutate(requestId);
    };

    const isLoading = isResponding || isCanceling;
    const mutationError = respondError || cancelError;

    return (
        <div>
            {/* Display Mutation Error */} 
            {mutationError && (
                <ErrorAlert message={(mutationError as any).message || 'Failed to process request.'} />
            )}

            {/* Request Cards */} 
            {requests.length > 0 ? (
                requests.map(request => (
                    <RequestCard 
                        key={request.id} 
                        request={request} 
                        type={type}
                        onAccept={type === 'received' ? handleAccept : undefined}
                        onDecline={type === 'received' ? handleDecline : undefined}
                        onCancel={type === 'sent' ? handleCancel : undefined}
                        isProcessing={isLoading}
                        processingId={processingId}
                    />
                ))
            ) : (
                <Alert variant="info">
                    {type === 'received' 
                        ? 'You have no pending connection requests.' 
                        : 'You have no pending sent requests.'}
                </Alert>
            )}
        </div>
    );
};

export default RequestList; 